import AddIcon from '@mui/icons-material/Add'
import AddLinkIcon from '@mui/icons-material/AddLink'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DoneIcon from '@mui/icons-material/Done'
import EastIcon from '@mui/icons-material/East'
import HistoryIcon from '@mui/icons-material/History'
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff'
import LinkSharpIcon from '@mui/icons-material/LinkSharp'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import RestoreIcon from '@mui/icons-material/Restore'
import VerifiedIcon from '@mui/icons-material/Verified'
import {
  alpha,
  Box,
  Button,
  IconButton,
  CircularProgress as MuiCircularProgress,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import CreateVersionFromDeal from 'components/CreateDeal/CreateVersionFromDeal'
import React, { useState } from 'react'

import ContractActionButton from '../../ui/contract-action-button'
import CreateContractIcon from '../../ui/create-contract-icon'
import GenerateLinksIcon from '../../ui/generate-links-icon'
import PandaIconBorder from '../../ui/panda-icon-border'
import PandaIconSvg from '../../ui/panda-icon-svg'
import RequestButtonTooltip from '../../ui/request-button-tooltip'
import ValidationTooltipContent from '../../ui/validation-tooltip-content'
import GenerateLinksModal from './GenerateLinksModal'
import ViewLinksModal from './ViewLinksModal'

import { useDraftDocument } from '../../../api/PandaDoc/useDraftDocument'
import { useGenerateLinks } from '../../../api/PandaDoc/useGenerateLinks'
import { useGetDocument } from '../../../api/PandaDoc/useGetDocument'
import { useSyncQueryCache } from '../../../api/aws/organizations_table/useSyncQueryCache'
import { useOrgHeartbeatLink } from '../../../api/aws/heartbeat/useOrgHeartbeatLink'

/** Size configuration for action buttons and icons */
export interface ActionSizeConfig {
  buttonSize: number
  iconSize: number
  muiIconSize: number
  badgeSize: number
}

export interface ContractActionsMenuProps {
  deal: any
  productRows: any[]
  productChanges: any
  activeUser: any
  isRequesting: boolean
  onRequestApproval: () => void
  onCreate: (e: React.MouseEvent<HTMLButtonElement>) => void
  validationErrors: Record<string, boolean>
  isCreating: boolean
  isValid: boolean
  isDisabled: boolean
  resetDealBaseline: () => void
  isSaveRequired: boolean
  /** Layout direction for the actions */
  direction?: 'row' | 'column'
  /** Spacing between action items */
  spacing?: number
  /** Custom size configuration - overrides responsive sizing */
  sizeConfig?: ActionSizeConfig
  /** Render actions as labeled text buttons instead of icon-only */
  labeledMode?: boolean
  /** When true, force icon-only mode (e.g. when AI agent panel is docked) */
  compact?: boolean
}

const ContractActionsMenu: React.FC<ContractActionsMenuProps> = ({
  deal,
  productRows,
  productChanges,
  activeUser,
  isRequesting,
  onRequestApproval,
  onCreate,
  validationErrors,
  isCreating,
  isValid,
  isDisabled,
  resetDealBaseline,
  isSaveRequired,
  direction = 'column',
  spacing = 2,
  sizeConfig,
  labeledMode = false,
  compact = false,
}) => {
  const theme = useTheme()
  const queryClient = useQueryClient()
  // Responsive sizing
  const isExtraLargeScreen = useMediaQuery(theme.breakpoints.up('xl'))
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))
  const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'))
  const isSmallScreen = useMediaQuery(theme.breakpoints.up('sm'))

  // Determine sizes based on screen size (can be overridden by sizeConfig prop)
  const getResponsiveSizes = (): ActionSizeConfig => {
    if (sizeConfig) return sizeConfig

    if (isExtraLargeScreen) {
      return { buttonSize: 56, iconSize: 50, muiIconSize: 44, badgeSize: 18 }
    }
    if (isLargeScreen) {
      return { buttonSize: 48, iconSize: 45, muiIconSize: 38, badgeSize: 16 }
    }
    if (isMediumScreen) {
      return { buttonSize: 44, iconSize: 40, muiIconSize: 34, badgeSize: 14 }
    }
    if (isSmallScreen) {
      return { buttonSize: 40, iconSize: 36, muiIconSize: 30, badgeSize: 12 }
    }
    // Extra small screens
    return { buttonSize: 36, iconSize: 32, muiIconSize: 26, badgeSize: 10 }
  }

  const sizes = getResponsiveSizes()

  // Local state
  const [isReverting, setIsReverting] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isLinksOpen, setIsLinksOpen] = useState(false)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [showVersionDialog, setShowVersionDialog] = useState(false)

  // Version button visibility
  const MSA_TYPES = ['MSA', 'RMSA', 'MSA - Housing', 'RMSA - Housing']
  const TERMINAL_STATUSES = ['Ended', 'Ended (system)', 'Cancelled', 'Replaced']
  const isMSAType = MSA_TYPES.includes(deal?.type ?? '')
  const isDealWon = deal?.stage === 'Deal Won'
  const isTerminal = TERMINAL_STATUSES.includes(deal?.dealStatus ?? '')
  const canCreateVersion = isMSAType && isDealWon && !isTerminal
  // No-signature deals are permanently isDraft=true — completion runs through
  // createInternalContract (sets isDraft) and there is no signing step to clear
  // it. Don't let that draft flag disable Create Version for them.
  const versionDisabledByDraft = !!deal?.isDraft && !deal?.noSignature

  // Hooks
  const { syncDealFields } = useSyncQueryCache()
  const { mutate: generateLinks } = useGenerateLinks(deal, activeUser)
  const { mutate: draftDocument } = useDraftDocument()
  const { document } = useGetDocument(deal?.contractId, ['document', deal?.contractId])
  // Links wait for the account's Heartbeat link; approval and contract
  // creation don't. Only a definite "not linked" holds the button, and the
  // send route refuses an unlinked account regardless.
  const { linked: isHeartbeatLinked } = useOrgHeartbeatLink(deal?.PK)
  const isHeartbeatUnlinked = isHeartbeatLinked === false

  // Derived state
  const isNoSignature = deal?.noSignature
  const isApproved = isNoSignature ? true : deal?.isApproved
  const saveRequired =
    productChanges?.created?.length > 0 || productChanges?.updated?.length > 0 || productChanges?.deletedIds?.length > 0
  const isRequestValid = isValid && !saveRequired

  // Use contractId as the reliable source of truth for whether a PandaDoc contract exists
  const hasContract = !!(deal?.contractId && deal.contractId !== '')

  // Stage checks
  const requestApprovalStage =
    !deal?.noSignature &&
    !isApproved &&
    !hasContract &&
    !['Completed', 'Completed Internally'].includes(deal?.status) &&
    !deal?.isRequested &&
    productRows?.length > 0

  const canCreateContract =
    isNoSignature && !hasContract && productRows?.length > 0
      ? true
      : isApproved && !hasContract && !deal?.isCompleted && productRows?.length > 0

  const revertState =
    document && document?.status === 'document.draft'
      ? false
      : isApproved && hasContract && document?.status !== 'document.completed'

  const isCompleted =
    isApproved && hasContract && ['Completed', 'Completed Internally'].includes(deal?.status) && !deal?.isDraft

  // Validation objects
  const generateLinksValidation = {
    ...validationErrors,
    ...(!hasContract && { contractCreated: !hasContract }),
    ...(!isApproved && !isNoSignature && { contractApproved: !isApproved }),
    ...(isHeartbeatUnlinked && { heartbeatAccountLink: true }),
  }

  const createContractValidation = {
    ...validationErrors,
    ...(!isApproved && !isNoSignature && { contractApproved: !isApproved }),
  }

  // Handlers
  const handleRevert = () => {
    if (!deal?.contractId) return
    setIsReverting(true)
    const updateData = { contractId: deal?.contractId, PK: deal?.PK, SK: deal?.SK }
    draftDocument(updateData, {
      onSuccess: () => {
        syncDealFields(queryClient, deal, [
          { ...(!isNoSignature && { fieldName: 'status', newValue: 'Draft' }) },
          { ...(!isNoSignature && { fieldName: 'stage', newValue: 'Proposal' }) },
          { fieldName: 'linksGenerated', newValue: false },
          { fieldName: 'isReverted', newValue: true },
          { fieldName: 'isCompleted', newValue: false },
          { fieldName: 'isDraft', newValue: true },
        ])
        queryClient.invalidateQueries({ queryKey: ['document', deal?.contractId] })
        requestAnimationFrame(() => {
          resetDealBaseline()
          setIsReverting(false)
        })
      },
      onError: () => setIsReverting(false),
    })
  }

  const handleGenerateLinks = () => {
    if (!deal?.contractId || !deal?.PK || !deal?.SK) return
    setIsGenerating(true)
    generateLinks(
      {
        contractId: deal.contractId,
        PK: deal.PK,
        SK: deal.SK,
        authorizedRecipient: deal?.recipients?.authorizedRecipient?.[0],
        contactRecipient: deal?.recipients?.contactRecipient?.[0],
      },
      {
        onSuccess: data => {
          const { authorizedDocumentLink, contactDocumentLink } = data?.links ?? {}
          syncDealFields(queryClient, deal, [
            { fieldName: 'contactDocumentLink', newValue: contactDocumentLink || '' },
            { fieldName: 'authorizedDocumentLink', newValue: authorizedDocumentLink || '' },
            { ...(!isNoSignature && { fieldName: 'status', newValue: 'Sent' }) },
            { ...(!isNoSignature && { fieldName: 'stage', newValue: 'Contract Sent' }) },
            { fieldName: 'dealStatus', newValue: 'In Progress' },
            { fieldName: 'linksGenerated', newValue: true },
            { fieldName: 'isDraft', newValue: false },
          ])
          queryClient.invalidateQueries({ queryKey: ['document', deal?.contractId] })
          requestAnimationFrame(() => {
            setIsGenerating(false)
            setIsConfirmationOpen(false)
            resetDealBaseline()
          })
        },
        onError: () => setIsGenerating(false),
      }
    )
  }

  const handleOpenLinks = () => setIsLinksOpen(true)
  const handleCloseLinks = () => setIsLinksOpen(false)
  const handleOpenConfirmation = () => setIsConfirmationOpen(true)
  const handleCloseConfirmation = () => setIsConfirmationOpen(false)
  const handleConfirmGenerateLinks = () => {
    setIsConfirmationOpen(false)
    handleGenerateLinks()
  }

  // Derived disabled state for request approval button
  const isRequestApprovalDisabled = isRequesting || !isValid || !requestApprovalStage

  // Get approval request badge content
  const getApprovalBadge = () => {
    if (deal?.isApproved) return <DoneIcon fontSize="small" sx={{ color: 'text.success' }} />
    if (deal?.isRequested)
      return <HistoryToggleOffIcon fontSize="small" sx={{ color: 'text.disabled', opacity: 0.5 }} />
    if (!deal?.isRequested) return <EastIcon fontSize="small" sx={{ color: 'text.warning' }} />
    return null
  }

  // Action configurations
  const actions = [
    {
      id: 'request-approval',
      label: deal?.isApproved ? 'Approved' : deal?.isRequested ? 'Pending' : 'Request Approval',
      visible: true,
      tooltip: (
        <RequestButtonTooltip
          newDeal={deal}
          isValid={isRequestValid}
          saveRequired={saveRequired}
          validationErrors={validationErrors}
        />
      ),
      icon: (
        <PeopleAltIcon
          sx={{
            fontSize: sizes.muiIconSize,
            color: isRequestApprovalDisabled ? 'text.disabled' : 'text.primary',
            opacity: isRequestApprovalDisabled ? 0.3 : 1,
          }}
        />
      ),
      badgeContent: getApprovalBadge(),
      onClick: onRequestApproval,
      isLoading: isRequesting,
      disabled: isRequestApprovalDisabled,
      isCompleted: deal?.isApproved,
    },
    {
      id: 'create-contract',
      label: hasContract ? 'Contract Created' : 'Create Contract',
      visible: true,
      tooltip: (
        <ValidationTooltipContent
          title="Create Contract"
          validationErrors={createContractValidation}
          saveRequired={saveRequired}
          isValid={isRequestValid && (isNoSignature || isApproved)}
          successMessage={hasContract ? undefined : 'Create Contract'}
          completedMessage="Contract Created"
          isCompleted={hasContract}
        />
      ),
      icon: (
        <CreateContractIcon
          disabled={isDisabled || !isRequestValid || !canCreateContract}
          isCompleted={hasContract}
          size={sizes.iconSize}
        />
      ),
      badgeContent: <AddIcon fontSize="small" sx={{ color: hasContract ? 'text.disabled' : 'text.primary' }} />,
      onClick: onCreate,
      isLoading: isCreating,
      disabled: isDisabled || !isRequestValid || !canCreateContract,
      isCompleted: hasContract,
    },
    {
      id: 'generate-links',
      label: deal?.linksGenerated ? 'Links Generated' : 'Generate Links',
      visible: true,
      tooltip: (
        <ValidationTooltipContent
          title="Generate Links"
          validationErrors={generateLinksValidation}
          saveRequired={saveRequired}
          isValid={isRequestValid && hasContract && !isHeartbeatUnlinked}
          completedMessage="Links Generated"
          isCompleted={deal?.linksGenerated}
        />
      ),
      icon: (
        <GenerateLinksIcon
          disabled={
            isGenerating ||
            isSaveRequired ||
            !isApproved ||
            !deal?.contractId ||
            !deal?.isDraft ||
            deal?.linksGenerated ||
            isHeartbeatUnlinked
          }
          isCompleted={deal?.linksGenerated}
          size={sizes.iconSize}
        />
      ),
      badgeContent: <AddLinkIcon sx={{ color: deal?.linksGenerated ? 'text.disabled' : '', zIndex: 10 }} />,
      onClick: handleOpenConfirmation,
      isLoading: isGenerating,
      disabled:
        isGenerating ||
        isSaveRequired ||
        !isApproved ||
        !deal?.contractId ||
        !deal?.isDraft ||
        deal?.linksGenerated ||
        isHeartbeatUnlinked,
      isCompleted: deal?.linksGenerated,
    },
    {
      id: 'revert-to-draft',
      label: 'Revert to Draft',
      visible: true,
      tooltip: (
        <>
          {document?.status !== 'document.completed' ? (
            !revertState ? (
              <Typography sx={{ color: 'text.warning' }}>Deal in Draft Status</Typography>
            ) : (
              <Typography>Revert to Draft</Typography>
            )
          ) : (
            <Typography sx={{ color: 'text.success' }}>Completed</Typography>
          )}
        </>
      ),
      icon: (
        <Box sx={{ opacity: !revertState ? 0.2 : 1 }}>
          <PandaIconSvg
            disabled={!revertState}
            bgColor={theme.palette.icon.warning}
            fgColor={theme.palette.text.primary}
            size={sizes.iconSize}
          />
        </Box>
      ),
      badgeContent: <RestoreIcon sx={{ height: sizes.badgeSize, width: sizes.badgeSize }} />,
      onClick: handleRevert,
      isLoading: isReverting,
      disabled: !revertState,
    },
    {
      id: 'view-links',
      label: 'View Links',
      visible: deal?.linksGenerated,
      tooltip: <Typography>View Links</Typography>,
      icon: <PandaIconBorder size={sizes.iconSize} />,
      badgeContent: <LinkSharpIcon sx={{ height: sizes.badgeSize, width: sizes.badgeSize }} />,
      onClick: handleOpenLinks,
      isLoading: false,
      disabled: false,
    },
    {
      id: 'create-version',
      label: 'Create Version',
      visible: canCreateVersion,
      tooltip: <Typography>Create Version</Typography>,
      icon: (
        <HistoryIcon
          sx={{
            fontSize: sizes.muiIconSize,
            color: versionDisabledByDraft ? 'text.disabled' : 'text.primary',
            opacity: versionDisabledByDraft ? 0.3 : 1,
          }}
        />
      ),
      badgeContent: <AddIcon fontSize="small" />,
      onClick: () => setShowVersionDialog(true),
      isLoading: false,
      disabled: versionDisabledByDraft,
    },
  ]

  // Below lg or when compact (agent docked), switch to icon-only mode in labeled layout
  const useIconOnly = !isExtraLargeScreen || compact

  const labeledButtonSx = {
    textTransform: 'none' as const,
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 2,
    px: useIconOnly ? 0.75 : 1.5,
    py: 0.5,
    minHeight: 34,
    minWidth: useIconOnly ? 34 : undefined,
    lineHeight: 1.2,
    whiteSpace: 'nowrap' as const,
  }

  const completeColor = (theme.palette.text as any)?.complete ?? theme.palette.success.main
  const linkColor = (theme.palette.text as any)?.link ?? theme.palette.primary.main

  const getActionColor = (action: (typeof actions)[number]) => {
    if (action.isCompleted) return 'success'
    if (action.id === 'revert-to-draft') return 'warning'
    return 'inherit'
  }

  // Get a clean icon for labeled mode (checkmark for completed, simpler action icons)
  const getLabeledIcon = (action: typeof actions[number]) => {
    if (action.isCompleted) return <CheckCircleOutlineIcon sx={{ fontSize: '16px !important' }} />
    switch (action.id) {
      case 'request-approval':
        return (
          <PeopleAltIcon
            sx={{ fontSize: '16px !important', color: action?.disabled ? 'text.disabled' : 'text.primary' }}
          />
        )
      case 'create-contract':
        return (
          <AddIcon sx={{ fontSize: '16px !important', color: action?.disabled ? 'text.disabled' : 'text.primary' }} />
        )
      case 'generate-links':
        return (
          <AddLinkIcon
            sx={{ fontSize: '16px !important', color: action?.disabled ? 'text.disabled' : 'text.primary' }}
          />
        )
      case 'revert-to-draft':
        return (
          <RestoreIcon
            sx={{ fontSize: '16px !important', color: action?.disabled ? 'text.disabled' : 'text.primary' }}
          />
        )
      case 'view-links':
        return (
          <LinkSharpIcon
            sx={{ fontSize: '16px !important', color: action?.disabled ? 'text.disabled' : 'text.primary' }}
          />
        )
      case 'create-version':
        return (
          <HistoryIcon
            sx={{ fontSize: '16px !important', color: action?.disabled ? 'text.disabled' : 'text.primary' }}
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: direction,
          gap: labeledMode ? 0.75 : spacing,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {labeledMode
          ? actions
              .filter(action => action.visible)
              .map(action => {
                const color = getActionColor(action)
                const isCompletedAction = action.isCompleted
                return (
                  <Tooltip key={action.id} title={action.tooltip} placement="bottom">
                    <span>
                      <Button
                        variant={isCompletedAction ? 'contained' : 'outlined'}
                        size="small"
                        onClick={action.onClick as any}
                        disabled={action.disabled}
                        color={color as any}
                        startIcon={
                          useIconOnly ? undefined : action.isLoading ? (
                            <MuiCircularProgress size={14} color="inherit" />
                          ) : (
                            getLabeledIcon(action)
                          )
                        }
                        sx={{
                          ...labeledButtonSx,
                          ...(isCompletedAction && {
                            bgcolor: alpha(completeColor, 0.15),
                            color: completeColor,
                            borderColor: alpha(completeColor, 0.3),
                            '&:hover': {
                              bgcolor: alpha(completeColor, 0.25),
                            },
                          }),
                          ...(!isCompletedAction && {
                            borderColor: alpha(theme.palette.text.primary, 0.12),
                            color: action.disabled ? 'text.disabled' : 'text.primary',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: alpha(linkColor, 0.06),
                            },
                          }),
                        }}
                      >
                        {useIconOnly ? (
                          action.isLoading ? (
                            <MuiCircularProgress size={14} color="inherit" />
                          ) : (
                            getLabeledIcon(action)
                          )
                        ) : (
                          action.label
                        )}
                      </Button>
                    </span>
                  </Tooltip>
                )
              })
          : actions
              .filter(action => action.visible)
              .map(action => (
                <ContractActionButton
                  key={action.id}
                  icon={action.icon}
                  tooltip={action.tooltip}
                  tooltipPlacement="bottom"
                  onClick={action.onClick}
                  isLoading={action.isLoading}
                  disabled={action.disabled}
                  isCompleted={action.isCompleted}
                  badgeContent={action.badgeContent}
                  width={sizes.buttonSize}
                  height={sizes.buttonSize}
                  iconSize={sizes.muiIconSize}
                />
              ))}

        {isCompleted && (
          <Tooltip title={<Typography>Completed</Typography>} placement="bottom">
            {labeledMode ? (
              <Button
                variant="contained"
                size="small"
                startIcon={useIconOnly ? undefined : <VerifiedIcon sx={{ fontSize: '16px !important' }} />}
                sx={{
                  ...labeledButtonSx,
                  bgcolor: alpha(completeColor, 0.15),
                  color: completeColor,
                  '&:hover': {
                    bgcolor: alpha(completeColor, 0.25),
                  },
                }}
              >
                {useIconOnly ? <VerifiedIcon sx={{ fontSize: 16 }} /> : 'Completed'}
              </Button>
            ) : (
              <IconButton
                sx={{
                  width: sizes.buttonSize,
                  height: sizes.buttonSize,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <VerifiedIcon sx={{ fontSize: sizes.muiIconSize, color: 'text.success' }} />
              </IconButton>
            )}
          </Tooltip>
        )}
      </Box>

      {/* Modals */}
      {isConfirmationOpen && (
        <GenerateLinksModal
          isOpen={isConfirmationOpen}
          onClose={handleCloseConfirmation}
          onConfirm={handleConfirmGenerateLinks}
          isGenerating={isGenerating}
        />
      )}

      {isLinksOpen && <ViewLinksModal isOpen={isLinksOpen} onClose={handleCloseLinks} deal={deal} />}

      {showVersionDialog && (
        <CreateVersionFromDeal
          open={showVersionDialog}
          onClose={() => setShowVersionDialog(false)}
          deal={deal}
          productRows={productRows}
        />
      )}
    </>
  )
}

export default ContractActionsMenu
